import { FC, useMemo, useState } from "react";
import { LuAlarmClock } from "react-icons/lu";

import { SettingsIconButton } from "../../../../components/button/SettingsIconButton";
import { SettingsDropdown } from "../../../../components/dropdown/SettingsDropdown";
import { SettingsConfirmModal } from "../../../../components/modal/SettingsConfirmModal";
import { DotsSvg } from "../../../../components/svg/DotsSvg";
import { EditPencilSvg } from "../../../../components/svg/EditPencilSvg";
import { TrashSvg } from "../../../../components/svg/TrashSvg";
import { WorkDetailsModel } from "../../../../factories/work/model/work-details.model";
import { WorkReminderModel } from "../../../../factories/work/model/work-reminder.model";
import { useAppSelector } from "../../../../redux/redux";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  useAddWorkReminderMutation,
  useDeleteWorkReminderMutation,
  useGetWorkRemindersQuery,
  useUpdateWorkReminderMutation,
} from "../../../../slices/WorkRemindersSlice";
import { AddReminderModal } from "./AddReminderModal";
import { ReminderDraft, ReminderItem } from "./reminders.mock";
import {
  formatReminderDate,
  getReminderDate,
  getReminderUserId,
  toIsoWithOffset,
} from "./reminders.utils";
import { useReminderLookups } from "./useReminderLookups";

type Props = {
  work: WorkDetailsModel;
};

const getDateBaseLabel = (dateBase: ReminderItem["dateBase"]) => {
  return dateBase === "dueDate" ? "due date" : "start date";
};

const mapReminderToDraft = (reminder: WorkReminderModel): ReminderDraft => ({
  days: `${reminder.days}`,
  direction: reminder.isBefore ? "before" : "after",
  dateBase: reminder.dueDate ? "dueDate" : "startDate",
  notifyUserId: getReminderUserId(reminder),
});

export const Reminders: FC<Props> = ({ work }) => {
  const lookups = useReminderLookups();
  const orgId = work.orgId || getLinkedFirm()?.orgId || "";
  const user = useAppSelector((state) => state.appReducer.user);

  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [editingReminder, setEditingReminder] =
    useState<WorkReminderModel | null>(null);
  const [deletingReminderId, setDeletingReminderId] = useState<string | null>(
    null,
  );

  const { data: reminders = [], isFetching } = useGetWorkRemindersQuery(
    { orgId, workId: work.id },
    { skip: !orgId || !work.id },
  );

  const [addReminder, { isLoading: isAddingReminder }] =
    useAddWorkReminderMutation();
  const [updateReminder, { isLoading: isUpdatingReminder }] =
    useUpdateWorkReminderMutation();
  const [deleteReminder, { isLoading: isDeletingReminder }] =
    useDeleteWorkReminderMutation();

  const visibleReminders = useMemo(() => {
    return reminders.map<ReminderItem>((reminder) => {
      const notifyUserId = getReminderUserId(reminder);
      const selectedUser = lookups.userOptions.find(
        (option) => option.value === notifyUserId,
      );

      return {
        id: reminder.id,
        workId: reminder.workId,
        notifyUserId,
        notifyUserName: selectedUser?.label || "a team member",
        days: reminder.days,
        direction: reminder.isBefore ? "before" : "after",
        dateBase: reminder.dueDate ? "dueDate" : "startDate",
        createdAt: reminder.createdAt,
        isSent: Boolean(reminder.isSend),
        goesOffAt: getReminderDate(
          {
            days: `${reminder.days}`,
            direction: reminder.isBefore ? "before" : "after",
            dateBase: reminder.dueDate ? "dueDate" : "startDate",
          },
          work,
        ),
      };
    });
  }, [lookups.userOptions, reminders, work]);

  // The server works out the day again from the work's dates; this tells it the time zone.
  const notificationDateFor = (draft: ReminderDraft) => {
    const date = getReminderDate(draft, work);
    return date ? toIsoWithOffset(date) : undefined;
  };

  const handleCreateReminder = async (draft: ReminderDraft) => {
    await addReminder({
      days: Number(draft.days),
      isBefore: draft.direction === "before",
      dueDate: draft.dateBase === "dueDate",
      orgId,
      workId: work.id,
      userIds: [draft.notifyUserId],
      createdByUserId: user.id,
      notificationDate: notificationDateFor(draft),
    }).unwrap();

    setIsOpenAdd(false);
  };

  const handleUpdateReminder = async (draft: ReminderDraft) => {
    if (!editingReminder) return;

    await updateReminder({
      ...editingReminder,
      days: Number(draft.days),
      isBefore: draft.direction === "before",
      dueDate: draft.dateBase === "dueDate",
      userIds: [draft.notifyUserId],
      createdByUserId: user.id,
      notificationDate: notificationDateFor(draft),
    }).unwrap();

    setEditingReminder(null);
  };

  const handleDeleteReminder = async () => {
    if (!deletingReminderId) return;
    await deleteReminder(deletingReminderId).unwrap();
    setDeletingReminderId(null);
  };

  return (
    <div className={"w-full p-6"}>
      <div className={"rounded-lg border border-gray-200 bg-white shadow-sm"}>
        <div
          className={
            "flex items-center justify-between border-b border-gray-200 px-4 py-3"
          }>
          <div className={"text-sm font-semibold text-[#253747]"}>
            Reminders
          </div>

          <button
            type={"button"}
            className={
              "flex h-[32px] items-center justify-center rounded-lg bg-[#7c66f0] px-4 py-2 font-Manrope text-sm font-semibold text-white"
            }
            onClick={() => setIsOpenAdd(true)}>
            Add reminder
          </button>
        </div>

        {isFetching ? (
          <div className={"flex h-[180px] items-center justify-center text-sm"}>
            Loading reminders
          </div>
        ) : visibleReminders.length === 0 ? (
          <div
            className={"flex h-[180px] flex-col items-center justify-center"}>
            <div
              className={
                "mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#F4F2FF] text-[#253747]"
              }>
              <LuAlarmClock size={24} />
            </div>

            <div className={"text-sm font-semibold text-[#253747]"}>
              There are no reminders
            </div>
          </div>
        ) : (
          <div>
            {visibleReminders.map((reminder) => (
              <div
                key={reminder.id}
                className={
                  "flex items-center justify-between border-b border-gray-100 px-4 py-4 last:border-b-0"
                }>
                <div
                  className={"flex items-center gap-3 text-sm text-[#253747]"}>
                  <LuAlarmClock size={18} />

                  <span>
                    Notify{" "}
                    <span className={"font-semibold"}>
                      {reminder.notifyUserName}
                    </span>{" "}
                    {reminder.days} {reminder.days === 1 ? "day" : "days"}{" "}
                    {reminder.direction} the{" "}
                    {getDateBaseLabel(reminder.dateBase)}
                    <span className={"ml-2 text-xs text-gray-500"}>
                      {reminder.isSent
                        ? "· Sent"
                        : reminder.goesOffAt
                        ? `· ${formatReminderDate(reminder.goesOffAt)}, 9:00 AM`
                        : ""}
                    </span>
                  </span>
                </div>

                <SettingsDropdown
                  actionsList={[
                    {
                      name: "Edit",
                      action: () => {
                        const selectedReminder = reminders.find(
                          (item) => item.id === reminder.id,
                        );

                        if (selectedReminder) {
                          setEditingReminder(selectedReminder);
                        }
                      },
                      icon: <EditPencilSvg />,
                    },
                    {
                      name: "Delete",
                      action: () => setDeletingReminderId(reminder.id),
                      icon: <TrashSvg />,
                      extraClasses: "text-[#DC2626]",
                      iconColor: "text-[#DC2626]",
                    },
                  ]}>
                  <SettingsIconButton
                    icon={<DotsSvg />}
                    onClick={() => null}
                    size={"small"}
                  />
                </SettingsDropdown>
              </div>
            ))}
          </div>
        )}
      </div>

      <AddReminderModal
        isOpen={isOpenAdd}
        isSaving={isAddingReminder}
        lookups={lookups}
        work={work}
        onClose={() => setIsOpenAdd(false)}
        onSave={handleCreateReminder}
      />

      <AddReminderModal
        isOpen={Boolean(editingReminder)}
        initialDraft={
          editingReminder ? mapReminderToDraft(editingReminder) : undefined
        }
        isSaving={isUpdatingReminder}
        lookups={lookups}
        work={work}
        onClose={() => setEditingReminder(null)}
        onSave={handleUpdateReminder}
        submitLabel={"Save"}
      />

      {deletingReminderId && (
        <SettingsConfirmModal
          icon={<TrashSvg />}
          title={"Delete reminder?"}
          description={"This reminder will be removed and won't be sent."}
          confirmText={"Delete"}
          variant={"danger"}
          isLoading={isDeletingReminder}
          onClose={() => setDeletingReminderId(null)}
          onConfirm={handleDeleteReminder}
        />
      )}
    </div>
  );
};
